import constate from 'constate'
import {
  FC, PropsWithChildren, useCallback, useEffect, useState
} from 'react'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '../constants/routes'
import { useAuthContext } from './useAuth'
import { useOnUpdate } from '../hooks/useOnUpdate'
import DatasetsStateProvider from '../components/features/datasets/components/DatasetsStateProvider'
import { ComparedVariablesProvider } from '../components/features/valuesComparison/state/useComparedVariablesState'

const APP_ROUTES = [
  ROUTES.DATASETS,
  ROUTES.COMPARE_DATASETS,
  ROUTES.KEYWORD_DETAILS,
  ROUTES.COMPARE_VARIABLES,
  ROUTES.COMPARE_VALUES,
  ROUTES.ADMIN_PANEL
]

export const isAppPath = (pathname: string) => APP_ROUTES.some(
  (route) => pathname === route || pathname.startsWith(`${route}/`)
)

const useAppSession = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthorized = !!useAuthContext().authData
  const [resetKey, setResetKey] = useState(0)
  const [lastAppLocation, setLastAppLocation] = useState<Location>()

  useEffect(() => {
    if (isAppPath(location.pathname)) {
      setLastAppLocation(location)
    }
  }, [location])

  const resetState = useCallback(() => {
    setResetKey((prevState) => prevState + 1)
    setLastAppLocation(undefined)
  }, [])

  useOnUpdate(() => {
    if (!isAuthorized) resetState()
  }, [isAuthorized])

  const returnToApp = useCallback(() => {
    if (!lastAppLocation) {
      navigate(ROUTES.DATASETS)
      return
    }
    const { pathname, search, hash, state } = lastAppLocation
    navigate({ pathname, search, hash }, { state })
  }, [lastAppLocation, navigate])

  const clearAll = useCallback(() => {
    resetState()
    navigate(ROUTES.DATASETS)
  }, [resetState, navigate])

  return {
    resetKey,
    returnToApp,
    clearAll
  }
}

const [AppSessionContextProvider, useAppSessionContext] = constate(useAppSession)

// Search state lives above the routes so it survives visits to the public pages.
// Changing `resetKey` remounts the providers, which clears all application state.
const AppStateProviders: FC<PropsWithChildren> = ({ children }) => {
  const { resetKey } = useAppSessionContext()
  return (
    <DatasetsStateProvider key={resetKey}>
      <ComparedVariablesProvider>
        {children}
      </ComparedVariablesProvider>
    </DatasetsStateProvider>
  )
}

export const AppSessionProvider: FC<PropsWithChildren> = ({ children }) => (
  <AppSessionContextProvider>
    <AppStateProviders>{children}</AppStateProviders>
  </AppSessionContextProvider>
)

export { useAppSessionContext }
